import React, { useState, useEffect } from "react";
import {
  Box,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridToolbar,
} from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

import debounce from "lodash/debounce";

import ParticipantDetailsDialog from "../../components/Admin/ParticipantDetailsDialog";
import { ParticipantData } from "../../models/Participant";
import participantService, {
  ParticipantResponse,
} from "../../services/participantService";

const DEFAULT_SORT_MODEL: GridSortModel = [
  {
    field: "lastName",
    sort: "asc",
  },
];

// Participant List Component
const ParticipantsPage = () => {
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [selectedParticipant, setSelectedParticipant] =
    useState<ParticipantData | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Fetch participants with debounced search
  const fetchParticipants = async (
    page: number,
    limit: number,
    sortBy?: string,
    sortOrder?: string,
    search?: string
  ) => {
    try {
      setLoading(true);
      const response: ParticipantResponse =
        await participantService.getAllParticipants(
          page + 1, // API typically uses 1-based pagination
          limit,
          sortBy,
          sortOrder,
          search
        );
      setParticipants(response.participants);
      setTotal(response.total); // Using total instead of totalCount
    } catch (error) {
      console.error("Error fetching participants:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedFetch = debounce((searchValue: string) => {
    const sortItem = sortModel[0];
    fetchParticipants(
      paginationModel.page,
      paginationModel.pageSize,
      sortItem?.field,
      sortItem?.sort?.toString(),
      searchValue
    );
  }, 300);

  // Effect to fetch data when pagination, sorting, or search changes
  useEffect(() => {
    const sortItem = sortModel[0];
    fetchParticipants(
      paginationModel.page,
      paginationModel.pageSize,
      sortItem?.field,
      sortItem?.sort?.toString(),
      searchTerm
    );
  }, [paginationModel, sortModel]);

  // Handle search input changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    debouncedFetch(newSearchTerm);
  };

  const columns: GridColDef[] = [
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    { field: "organization", headerName: "Organization", flex: 1.5 },
    { field: "positionTitle", headerName: "Position", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            onClick={() => handleViewDetails(params.row)}
            size="small"
            color="primary"
          >
            <FontAwesomeIcon icon={faEdit} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleViewDetails = (participant: ParticipantData) => {
    setSelectedParticipant(participant);
    setIsDetailsOpen(true);
  };

  return (
    <Box sx={{ height: "100%", width: "100%", p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h4" gutterBottom>
            Participant Management
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search participants..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ height: 600 }}>
            <DataGrid
              rows={participants}
              columns={columns}
              rowCount={total}
              loading={loading}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              initialState={{
                sorting: {
                  sortModel: DEFAULT_SORT_MODEL,
                },
              }}
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                },
              }}
              sx={{
                "& .MuiDataGrid-toolbarContainer": {
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  p: 1,
                },
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {selectedParticipant && (
        <ParticipantDetailsDialog
          participant={selectedParticipant}
          open={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
        />
      )}
    </Box>
  );
};

export default ParticipantsPage;
